/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { get } from 'lodash';
import { AlertBar, BrandColors, LoadingSpinner, Tabs } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { getConfig } from 'blb-utils/config';
import { getUserGroups, getUserProfile } from 'blb-api/users';
import { errorMessage } from 'blb-utils/errors';
import { MeContext } from 'blb-contexts/Me';
import { dashboardHorizontalPadding } from 'blb-styles/constants';
import { ProfileInfo } from './ProfileInfo';
import { CredentialUpload } from './CredentialUpload';
import { GroupsInfo } from './GroupsInfo';
import { getPersona, isProfilePersona } from 'blb-persona/index';
import { CredentialInfo } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/CredentialInfo';
import { Box } from 'grommet';
import styled from 'styled-components';

export type TabType = 'Profile' | 'Branches' | 'Credentials' | 'Journal' | 'Room Assignment';

const ContentWrapper = styled(Box)`
  background-color: ${BrandColors.BACKGROUND_GREY};
  padding: 32px;
`;

export const Header = styled.div({
  borderBottom: `1px solid ${BrandColors.CLOUD_50}`,
  paddingLeft: dashboardHorizontalPadding,
  paddingRight: dashboardHorizontalPadding,
  paddingTop: 32,
});

export const ProfilePage: React.FC<{}> = () => {
  const [activeTab, setActiveTab] = useState<TabType>('Profile');
  const [userInfo, setUserInfo] = useState<BlendBridge.API.UserProfile | null>(null);
  const [userGroups, setUserGroups] = useState<BlendBridge.API.SearchGroupRecord[] | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [loadingError, setLoadingError] = useState<string>();

  const me = useContext(MeContext)!;
  const { isBranchTabEnabled } = getConfig();

  const persona = getPersona(me.principalType);
  const tabsForPersona = persona.getProfileTabs(me);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const { data: fetchedUserInfo } = await getUserProfile(me.principalId);
      setUserInfo(fetchedUserInfo);
      if (isBranchTabEnabled && tabsForPersona.includes('Branches')) {
        const { data: fetchedUserGroups } = await getUserGroups(me.principalId);
        setUserGroups(fetchedUserGroups);
      }
    } catch (error) {
      setLoadingError(get(error, 'response.data', errorMessage('LOADING_PAGE', me.principalType)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchUserInfo();
  }, []);

  const tabsConfig = React.useMemo(
    () =>
      tabsForPersona.map(tabName => ({
        active: activeTab === tabName,
        disabled: false,
        id: tabName,
        label: tabName,
        onClick: () => setActiveTab(tabName),
      })),
    [me],
  );

  const pageContent = React.useMemo(() => {
    switch (activeTab) {
      case 'Profile':
        return userInfo && <ProfileInfo fetchUserInfo={fetchUserInfo} userInfo={userInfo} />;
      case 'Branches':
        return userGroups && <GroupsInfo userGroups={userGroups} />;
      case 'Credentials': {
        return getConfig().isNewOnboardingEnabledForBSRNotary ? <CredentialInfo /> : <CredentialUpload />;
      }
    }
  }, [activeTab, userInfo, userGroups]);

  const renderBody = () => {
    if (loadingError) {
      return (
        <ContentWrapper flex={true}>
          <AlertBar message={loadingError} theme="error" />
        </ContentWrapper>
      );
    }
    if (isLoading) {
      return (
        <Box align="center" flex={true} justify="center">
          <LoadingSpinner size="lg" theme="secondary" />
        </Box>
      );
    }
    return (
      <ContentWrapper flex={true} overflow="auto">
        {pageContent}
      </ContentWrapper>
    );
  };

  return (
    <Box fill="vertical">
      <Header>
        {isProfilePersona(persona) && persona.renderProfileHeader()}
        <Tabs activeTab={activeTab} tabsConfig={tabsConfig} />
      </Header>
      {renderBody()}
    </Box>
  );
};
